import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, MailCheck } from "lucide-react";

/**
 * VerifyEmail
 * -----------
 * Public landing page hit from the link inside the verification email.
 * Posts the token to /auth/verify-email and shows one of:
 *   - loading
 *   - success (verified or already_verified)
 *   - error (invalid/expired/used)
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    let alive = true;
    if (!token) {
      setState({ status: "error", message: "Missing verification token in the link." });
      return;
    }
    axios.post(`${API}/auth/verify-email`, { token })
      .then((r) => { if (alive) setState({ status: "success", data: r.data }); })
      .catch((err) => {
        if (alive) {
          setState({
            status: "error",
            message: err?.response?.data?.detail || "Could not verify your email. Please try again.",
          });
        }
      });
    return () => { alive = false; };
  }, [token]);

  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900 p-6" data-testid="verify-email-page">
      <Card className="w-full max-w-md p-8 sm:p-10 text-center border-slate-200">
        {state.status === "loading" && (
          <div data-testid="verify-loading">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-slate-100 grid place-items-center mb-4">
              <Loader2 className="w-7 h-7 animate-spin text-slate-500" />
            </div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Verifying your email…</h1>
            <p className="text-slate-600 mt-2">Hold tight, this only takes a moment.</p>
          </div>
        )}

        {state.status === "success" && (
          <div data-testid="verify-success">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-100 grid place-items-center mb-4">
              {state.data?.status === "already_verified"
                ? <MailCheck className="w-8 h-8 text-emerald-700" />
                : <CheckCircle2 className="w-8 h-8 text-emerald-700" />}
            </div>
            <h1 className="font-display text-2xl font-bold tracking-tight">
              {state.data?.status === "already_verified" ? "Already verified" : "Email verified!"}
            </h1>
            <p className="text-slate-600 mt-2">
              {state.data?.message || "Your email has been verified. An admin will review your account shortly."}
            </p>
            <Link to="/login">
              <Button className="w-full mt-6 h-11 bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="verify-go-login-btn">
                Go to login
              </Button>
            </Link>
          </div>
        )}

        {state.status === "error" && (
          <div data-testid="verify-error">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-rose-100 grid place-items-center mb-4">
              <XCircle className="w-8 h-8 text-rose-700" />
            </div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Verification failed</h1>
            <p className="text-slate-600 mt-2">{state.message}</p>
            <Link to="/login">
              <Button variant="outline" className="w-full mt-6 h-11" data-testid="verify-back-login-btn">
                Back to login
              </Button>
            </Link>
            <p className="mt-3 text-xs text-slate-500">
              If your link expired, log in and click "Resend verification email."
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
